import { safeLocalStorage, safeSessionStorage } from "@/utils/safeStorage";
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const AFFILIATE_REF_KEY = 'affiliate_ref';
const AFFILIATE_TRACKED_KEY = 'affiliate_ref_tracked';

/**
 * Hook to track affiliate referrals from URL ?ref= parameter.
 * Should be called once at app root level.
 * 
 * - Reads ?ref= from URL
 * - Stores in localStorage
 * - Calls affiliate-track-click edge function
 * - Cleans URL without page reload
 */
export function useAffiliateTracking() {
  const tracked = useRef(false);

  useEffect(() => {
    // Only run once
    if (tracked.current) return;
    tracked.current = true;

    const url = new URL(window.location.href);
    const ref = url.searchParams.get('ref');

    if (!ref) return;

    // Check if we already tracked this ref in this session
    const alreadyTracked = safeSessionStorage.getItem(AFFILIATE_TRACKED_KEY);
    if (alreadyTracked === ref) return;

    // Store ref in localStorage for signup flow
    safeLocalStorage.setItem(AFFILIATE_REF_KEY, ref);

    // Track the click (fire and forget)
    trackClick(ref);

    // Mark as tracked in this session
    safeSessionStorage.setItem(AFFILIATE_TRACKED_KEY, ref);

    // Clean URL without page reload
    url.searchParams.delete('ref');
    window.history.replaceState({}, '', url.toString());
  }, []);
}

async function trackClick(ref: string) {
  try {
    const { data, error } = await supabase.functions.invoke('affiliate-track-click', {
      body: { ref },
    });

    if (error) {
      console.error('[Affiliate] Failed to track click:', error);
    } else {
      console.log('[Affiliate] Click tracked:', data);
    }
  } catch (err) {
    console.error('[Affiliate] Error tracking click:', err);
  }
}

/**
 * Get stored affiliate ref code (if any).
 */
export function getAffiliateRef(): string | null {
  return safeLocalStorage.getItem(AFFILIATE_REF_KEY);
}

/**
 * Clear affiliate ref after it's been consumed.
 */
export function clearAffiliateRef(): void {
  safeLocalStorage.removeItem(AFFILIATE_REF_KEY);
}

/**
 * Check if affiliate ref has already been consumed for this user.
 */
export function isAffiliateRefConsumed(): boolean {
  return safeLocalStorage.getItem('affiliate_ref_consumed') === 'true';
}

/**
 * Mark affiliate ref as consumed.
 */
export function markAffiliateRefConsumed(): void {
  safeLocalStorage.setItem('affiliate_ref_consumed', 'true');
}
